import * as THREE from 'three';
import { BrepFace, BrepSurface } from './FikoBrep';

/**
 * Yüzey Tanıma Algoritmaları - RANSAC ve Least Squares Fitting
 *
 * Bu modül mesh face'lerinden geometrik yüzey tiplerini tanır:
 * - Plane (Düzlem)
 * - Cylinder (Silindir)
 * - Sphere (Küre)
 * - Cone (Koni)
 * - Torus (Halka)
 */

export class FikoBrepSurfaceRecognition {
    private tolerance: number;

    constructor(tolerance: number = 1e-6) {
        this.tolerance = tolerance;
    }

    /**
     * Face listesinden yüzey tipini tanımaya çalışır
     */
    public recognizeSurface(face: BrepFace, points: THREE.Vector3[]): BrepSurface | null {
        if (points.length < 3) return null;

        // 1. Düzlem testi (en basit)
        const planeResult = this.fitPlane(points);
        if (planeResult.error < this.tolerance * 10) {
            return {
                type: 'plane',
                origin: planeResult.origin,
                normal: planeResult.normal,
                uAxis: this.computeUAxis(planeResult.normal),
                vAxis: this.computeVAxis(planeResult.normal)
            };
        }

        // 2. Silindir testi
        const cylinderResult = this.fitCylinder(points);
        if (cylinderResult.error < this.tolerance * 20) {
            return {
                type: 'cylinder',
                axis: cylinderResult.axis,
                radius: cylinderResult.radius,
                center: cylinderResult.center,
                height: cylinderResult.height
            };
        }

        // 3. Küre testi
        const sphereResult = this.fitSphere(points);
        if (sphereResult.error < this.tolerance * 15) {
            return {
                type: 'sphere',
                center: sphereResult.center,
                radius: sphereResult.radius
            };
        }

        // 4. Koni testi
        const coneResult = this.fitCone(points);
        if (coneResult.error < this.tolerance * 25) {
            // radius = height * tan(angle)
            const radius = coneResult.height * Math.tan(coneResult.angle);
            return {
                type: 'cone',
                apex: coneResult.apex,
                axis: coneResult.axis,
                radius: radius,
                angle: coneResult.angle,
                height: coneResult.height
            };
        }

        // 5. Torus testi
        const torusResult = this.fitTorus(points);
        if (torusResult.error < this.tolerance * 25) {
            return {
                type: 'torus',
                center: torusResult.center,
                axis: torusResult.axis,
                majorRadius: torusResult.majorRadius,
                minorRadius: torusResult.minorRadius
            };
        }

        // Taninamadi -> freeform
        return {
            type: 'freeform',
            triangles: []
        };
    }

    // ========== PLANE FİTTİNG (En Küçük Kareler) ==========

    /**
     * Least Squares Plane Fitting
     * Ax + By + Cz + D = 0 formunda düzlem bulur
     */
    public fitPlane(points: THREE.Vector3[]): {
        origin: THREE.Vector3;
        normal: THREE.Vector3;
        error: number;
    } {
        if (points.length < 3) {
            return {
                origin: new THREE.Vector3(),
                normal: new THREE.Vector3(0, 0, 1),
                error: Infinity
            };
        }

        // Merkez nokta hesapla
        const centroid = new THREE.Vector3();
        points.forEach(p => centroid.add(p));
        centroid.divideScalar(points.length);

        // Kovaryans matrisi (3x3)
        let xx = 0, xy = 0, xz = 0;
        let yy = 0, yz = 0, zz = 0;

        points.forEach(p => {
            const dx = p.x - centroid.x;
            const dy = p.y - centroid.y;
            const dz = p.z - centroid.z;

            xx += dx * dx;
            xy += dx * dy;
            xz += dx * dz;
            yy += dy * dy;
            yz += dy * dz;
            zz += dz * dz;
        });

        // Determinant hesaplama (3x3 için simplified)
        const det_x = yy * zz - yz * yz;
        const det_y = xx * zz - xz * xz;
        const det_z = xx * yy - xy * xy;

        const det_max = Math.max(det_x, det_y, det_z);

        let normal: THREE.Vector3;

        // En büyük determinantı kullan (numerik kararlılık için)
        if (det_max === det_x) {
            normal = new THREE.Vector3(det_x, xz * yz - xy * zz, xy * yz - xz * yy);
        } else if (det_max === det_y) {
            normal = new THREE.Vector3(xz * yz - xy * zz, det_y, xy * xz - yz * xx);
        } else {
            normal = new THREE.Vector3(xy * yz - xz * yy, xy * xz - yz * xx, det_z);
        }

        normal.normalize();

        // Hata hesaplama (noktaların düzlemden uzaklıklarının karelerinin toplamı)
        let error = 0;
        points.forEach(p => {
            const dist = Math.abs(normal.dot(p.clone().sub(centroid)));
            error += dist * dist;
        });
        error = Math.sqrt(error / points.length);

        return {
            origin: centroid,
            normal: normal,
            error: error
        };
    }

    // ========== CYLINDER FİTTİNG (RANSAC + Least Squares) ==========

    /**
     * RANSAC Cylinder Fitting
     * Axis + radius bulur
     */
    public fitCylinder(points: THREE.Vector3[]): {
        axis: THREE.Vector3;
        radius: number;
        center: THREE.Vector3;
        height: number;
        error: number;
    } {
        if (points.length < 5) {
            return {
                axis: new THREE.Vector3(0, 0, 1),
                radius: 0,
                center: new THREE.Vector3(),
                height: 0,
                error: Infinity
            };
        }

        // RANSAC parametreleri
        const maxIterations = 100;
        const inlierThreshold = this.tolerance * 5;
        let bestAxis = new THREE.Vector3(0, 0, 1);
        let bestRadius = 0;
        let bestCenter = new THREE.Vector3();
        let bestInliers = 0;

        for (let iter = 0; iter < maxIterations; iter++) {
            // 3 rastgele nokta seç
            const p1 = points[Math.floor(Math.random() * points.length)];
            const p2 = points[Math.floor(Math.random() * points.length)];
            const p3 = points[Math.floor(Math.random() * points.length)];

            // Axis tahmini (p1-p2 hattı)
            const axis = p2.clone().sub(p1).normalize();

            // Radius tahmini (p3'ün p1-p2 hattına uzaklığı)
            const center = p1.clone().add(p2).multiplyScalar(0.5);
            const toP3 = p3.clone().sub(center);
            const perpDist = toP3.clone().sub(axis.clone().multiplyScalar(toP3.dot(axis))).length();

            // Inlier sayısını hesapla
            let inliers = 0;
            points.forEach(p => {
                const toP = p.clone().sub(center);
                const radialDist = toP.clone().sub(axis.clone().multiplyScalar(toP.dot(axis))).length();
                if (Math.abs(radialDist - perpDist) < inlierThreshold) {
                    inliers++;
                }
            });

            if (inliers > bestInliers) {
                bestInliers = inliers;
                bestAxis = axis;
                bestRadius = perpDist;
                bestCenter = center;
            }
        }

        // Merkezi eksen uzerine projekte et (RANSAC orta noktasi eksen uzerinde olmayabilir)
        const centroid = new THREE.Vector3();
        points.forEach(p => centroid.add(p));
        centroid.divideScalar(points.length);
        const centroidToCenter = centroid.clone().sub(bestCenter);
        const axisProj = bestAxis.clone().multiplyScalar(centroidToCenter.dot(bestAxis));
        const radialOffset = centroidToCenter.clone().sub(axisProj);
        bestCenter.add(radialOffset);

        // Yukseklik hesaplama
        let minProj = Infinity;
        let maxProj = -Infinity;
        points.forEach(p => {
            const proj = bestAxis.dot(p.clone().sub(bestCenter));
            minProj = Math.min(minProj, proj);
            maxProj = Math.max(maxProj, proj);
        });
        const height = maxProj - minProj;

        // Hata hesaplama
        let error = 0;
        points.forEach(p => {
            const toP = p.clone().sub(bestCenter);
            const radialDist = toP.clone().sub(bestAxis.clone().multiplyScalar(toP.dot(bestAxis))).length();
            error += Math.pow(radialDist - bestRadius, 2);
        });
        error = Math.sqrt(error / points.length);

        return {
            axis: bestAxis,
            radius: bestRadius,
            center: bestCenter,
            height: height,
            error: error
        };
    }

    // ========== SPHERE FİTTİNG (Least Squares) ==========

    /**
     * Least Squares Sphere Fitting
     * (x - cx)² + (y - cy)² + (z - cz)² = r²
     */
    public fitSphere(points: THREE.Vector3[]): {
        center: THREE.Vector3;
        radius: number;
        error: number;
    } {
        if (points.length < 4) {
            return {
                center: new THREE.Vector3(),
                radius: 0,
                error: Infinity
            };
        }

        // İlk tahmin: noktaların merkezi
        const centroid = new THREE.Vector3();
        points.forEach(p => centroid.add(p));
        centroid.divideScalar(points.length);

        // Gauss-Newton iterasyonu (simplified)
        let cx = centroid.x;
        let cy = centroid.y;
        let cz = centroid.z;

        for (let iter = 0; iter < 10; iter++) {
            // Radius hesaplama
            let sumR = 0;
            points.forEach(p => {
                sumR += Math.sqrt(
                    (p.x - cx) ** 2 + (p.y - cy) ** 2 + (p.z - cz) ** 2
                );
            });
            const radius = sumR / points.length;

            // Merkez güncelleme (simplified)
            let sumX = 0, sumY = 0, sumZ = 0;
            points.forEach(p => {
                const dist = Math.sqrt((p.x - cx) ** 2 + (p.y - cy) ** 2 + (p.z - cz) ** 2);
                if (dist > 1e-10) {
                    const w = 1 / dist;
                    sumX += w * p.x;
                    sumY += w * p.y;
                    sumZ += w * p.z;
                }
            });
            cx = sumX / points.length;
            cy = sumY / points.length;
            cz = sumZ / points.length;
        }

        const center = new THREE.Vector3(cx, cy, cz);

        // Final radius
        let sumR = 0;
        points.forEach(p => {
            sumR += p.distanceTo(center);
        });
        const radius = sumR / points.length;

        // Hata hesaplama
        let error = 0;
        points.forEach(p => {
            error += Math.pow(p.distanceTo(center) - radius, 2);
        });
        error = Math.sqrt(error / points.length);

        return { center, radius, error };
    }

    // ========== CONE FİTTİNG (RANSAC + Least Squares) ==========

    /**
     * RANSAC Cone Fitting
     * Apex + axis + angle bulur
     *
     * Algoritma:
     * 1. RANSAC ile apex adayları bul (noktalardan çıkan ışınların kesişimi)
     * 2. Her apex için axis ve angle optimize et
     * 3. En düşük hatalı sonucu seç
     */
    public fitCone(points: THREE.Vector3[]): {
        apex: THREE.Vector3;
        axis: THREE.Vector3;
        angle: number;
        height: number;
        error: number;
    } {
        if (points.length < 6) {
            return {
                apex: new THREE.Vector3(),
                axis: new THREE.Vector3(0, 0, 1),
                angle: Math.PI / 4,
                height: 0,
                error: Infinity
            };
        }

        // RANSAC parametreleri
        const maxIterations = 150;
        const inlierThreshold = this.tolerance * 10;

        let bestApex = new THREE.Vector3();
        let bestAxis = new THREE.Vector3(0, 0, 1);
        let bestAngle = Math.PI / 4;
        let bestInliers = 0;
        let bestError = Infinity;

        // Noktaların merkezi (ilk tahmin için)
        const centroid = new THREE.Vector3();
        points.forEach(p => centroid.add(p));
        centroid.divideScalar(points.length);

        // Normal'leri hesapla (varsa mesh normal attribute'larından, yoksa yaklaşık)
        const normals = this.estimateNormals(points);

        for (let iter = 0; iter < maxIterations; iter++) {
            // 1. Rastgele 3 nokta seç
            const idx1 = Math.floor(Math.random() * points.length);
            const idx2 = Math.floor(Math.random() * points.length);
            const idx3 = Math.floor(Math.random() * points.length);

            if (idx1 === idx2 || idx2 === idx3 || idx1 === idx3) continue;

            const p1 = points[idx1];
            const p2 = points[idx2];
            const p3 = points[idx3];

            const n1 = normals[idx1];
            const n2 = normals[idx2];
            const n3 = normals[idx3];

            // 2. Apex tahmini: normal'lerin tersinin kesişim noktası
            // Her noktadan normal yönünde ışın çıkar, apex bunların kesişimi
            const apex = this.findApexFromNormals(p1, n1, p2, n2, p3, n3);
            if (!apex) continue;

            // 3. Axis tahmini: apex'ten merkeze doğru
            const axis = centroid.clone().sub(apex).normalize();
            if (axis.lengthSq() < 1e-10) continue;

            // 4. Açı tahmini: apex'ten noktaya olan ışının axis ile açısı
            let sumAngle = 0;
            let angleCount = 0;
            points.forEach(p => {
                const toP = p.clone().sub(apex);
                if (toP.lengthSq() > 1e-10) {
                    const angle = Math.acos(Math.max(-1, Math.min(1, toP.normalize().dot(axis))));
                    sumAngle += angle;
                    angleCount++;
                }
            });
            const angle = angleCount > 0 ? sumAngle / angleCount : Math.PI / 4;

            // 5. Inlier sayısı ve hata hesaplama
            let inliers = 0;
            let totalError = 0;

            points.forEach(p => {
                const toP = p.clone().sub(apex);
                const distFromApex = toP.length();

                // Cone yüzeyinde olması gereken yarıçap
                const axialDist = toP.dot(axis);
                const expectedRadius = axialDist * Math.tan(angle);

                // Gerçek radyal uzaklık
                const radialVec = toP.clone().sub(axis.clone().multiplyScalar(axialDist));
                const actualRadius = radialVec.length();

                const error = Math.abs(actualRadius - expectedRadius);

                if (error < inlierThreshold) {
                    inliers++;
                }
                totalError += error * error;
            });

            totalError = Math.sqrt(totalError / points.length);

            if (inliers > bestInliers || (inliers === bestInliers && totalError < bestError)) {
                bestInliers = inliers;
                bestApex = apex.clone();
                bestAxis = axis.clone();
                bestAngle = angle;
                bestError = totalError;
            }
        }

        // Least Squares refinement (isteğe bağlı)
        if (bestInliers > points.length * 0.5) {
            const refined = this.refineConeParameters(points, bestApex, bestAxis, bestAngle);
            bestApex = refined.apex;
            bestAxis = refined.axis;
            bestAngle = refined.angle;
            bestError = refined.error;
        }

        // Yükseklik hesaplama
        let minProj = Infinity;
        let maxProj = -Infinity;
        points.forEach(p => {
            const toP = p.clone().sub(bestApex);
            const proj = toP.dot(bestAxis);
            if (proj > 0) { // Sadece apex'ten uzaktaki noktalar
                minProj = Math.min(minProj, proj);
                maxProj = Math.max(maxProj, proj);
            }
        });
        const height = maxProj > minProj ? maxProj - minProj : 0;

        return {
            apex: bestApex,
            axis: bestAxis,
            angle: bestAngle,
            height: height,
            error: bestError
        };
    }

    /**
     * Normal vektörlerini tahmin et (yaklaşık - mesh normal'leri yoksa)
     */
    private estimateNormals(points: THREE.Vector3[]): THREE.Vector3[] {
        const normals: THREE.Vector3[] = [];
        const k = Math.min(6, points.length - 1); // k-nearest neighbors

        points.forEach((p, i) => {
            // En yakın k noktayı bul
            const neighbors: { dist: number; idx: number }[] = [];
            points.forEach((q, j) => {
                if (i !== j) {
                    neighbors.push({ dist: p.distanceToSquared(q), idx: j });
                }
            });
            neighbors.sort((a, b) => a.dist - b.dist);

            // Kovaryans matrisi ile normal hesapla
            const nearestPoints = neighbors.slice(0, k).map(n => points[n.idx]);
            const planeResult = this.fitPlane([p, ...nearestPoints]);
            normals.push(planeResult.normal);
        });

        return normals;
    }

    /**
     * 3 nokta ve normalden apex bul (ışınların kesişimi)
     */
    private findApexFromNormals(
        p1: THREE.Vector3, n1: THREE.Vector3,
        p2: THREE.Vector3, n2: THREE.Vector3,
        p3: THREE.Vector3, n3: THREE.Vector3
    ): THREE.Vector3 | null {
        // Normal'lerin ters yönünde ışınlar
        // Apex: bu 3 ışının en yakın kesişim noktası

        // İki ışının en yakın noktalarının ortası
        const apex12 = this.closestPointOnTwoRays(p1, n1.clone().negate(), p2, n2.clone().negate());
        const apex23 = this.closestPointOnTwoRays(p2, n2.clone().negate(), p3, n3.clone().negate());

        if (!apex12 || !apex23) return null;

        // Ortalama apex
        return apex12.add(apex23).multiplyScalar(0.5);
    }

    /**
     * İki ışının en yakın noktası
     * Ray1: p1 + t1 * d1
     * Ray2: p2 + t2 * d2
     */
    private closestPointOnTwoRays(
        p1: THREE.Vector3, d1: THREE.Vector3,
        p2: THREE.Vector3, d2: THREE.Vector3
    ): THREE.Vector3 | null {
        const w0 = p1.clone().sub(p2);

        const a = d1.dot(d1);
        const b = d1.dot(d2);
        const c = d2.dot(d2);
        const d = d1.dot(w0);
        const e = d2.dot(w0);

        const denom = a * c - b * b;
        if (Math.abs(denom) < 1e-10) return null; // Paralel ışınlar

        const t1 = (b * e - c * d) / denom;
        const t2 = (a * e - b * d) / denom;

        // Her iki ışın üzerindeki en yakın noktalar
        const closest1 = p1.clone().add(d1.clone().multiplyScalar(t1));
        const closest2 = p2.clone().add(d2.clone().multiplyScalar(t2));

        // Orta nokta
        return closest1.add(closest2).multiplyScalar(0.5);
    }

    /**
     * Cone parametrelerini Gauss-Newton ile refine et
     */
    private refineConeParameters(
        points: THREE.Vector3[],
        apex: THREE.Vector3,
        axis: THREE.Vector3,
        angle: number
    ): { apex: THREE.Vector3; axis: THREE.Vector3; angle: number; error: number } {
        // Simplified Gauss-Newton (5 iterasyon)
        let currentApex = apex.clone();
        let currentAxis = axis.clone();
        let currentAngle = angle;

        for (let iter = 0; iter < 5; iter++) {
            // Gradient descent step
            const deltaApex = new THREE.Vector3();
            const deltaAxis = new THREE.Vector3();
            let deltaAngle = 0;

            points.forEach(p => {
                const toP = p.clone().sub(currentApex);
                const axialDist = toP.dot(currentAxis);

                if (axialDist > 1e-6) {
                    const expectedRadius = axialDist * Math.tan(currentAngle);
                    const radialVec = toP.clone().sub(currentAxis.clone().multiplyScalar(axialDist));
                    const actualRadius = radialVec.length();
                    const error = actualRadius - expectedRadius;

                    // Apex'i error yönünde kaydır
                    if (radialVec.lengthSq() > 1e-10) {
                        deltaApex.add(radialVec.normalize().multiplyScalar(error * 0.01));
                    }

                    // Açıyı ayarla
                    deltaAngle += error * 0.001;
                }
            });

            currentApex.sub(deltaApex.divideScalar(points.length));
            currentAngle -= deltaAngle / points.length;

            // Açı sınırları
            currentAngle = Math.max(0.01, Math.min(Math.PI / 2 - 0.01, currentAngle));
        }

        // Final error
        let error = 0;
        points.forEach(p => {
            const toP = p.clone().sub(currentApex);
            const axialDist = toP.dot(currentAxis);
            const expectedRadius = axialDist * Math.tan(currentAngle);
            const radialVec = toP.clone().sub(currentAxis.clone().multiplyScalar(axialDist));
            const actualRadius = radialVec.length();
            error += Math.pow(actualRadius - expectedRadius, 2);
        });
        error = Math.sqrt(error / points.length);

        return {
            apex: currentApex,
            axis: currentAxis,
            angle: currentAngle,
            error: error
        };
    }

    // ========== TORUS FİTTİNG (PCA + Circle Fitting) ==========

    /**
     * Torus Fitting
     *
     * Algoritma:
     * 1. PCA ile torus simetri eksenini bul (en kucuk varyans yonu)
     * 2. Noktalari eksen diklemine projekte et -> major radius (merkez cember)
     * 3. Her noktanin merkez cemberden uzakligini hesapla -> minor radius
     * 4. Sapma kontrolu ile dogrula
     */
    public fitTorus(points: THREE.Vector3[]): {
        center: THREE.Vector3;
        axis: THREE.Vector3;
        majorRadius: number;
        minorRadius: number;
        error: number;
    } {
        const fail = {
            center: new THREE.Vector3(),
            axis: new THREE.Vector3(0, 0, 1),
            majorRadius: 0,
            minorRadius: 0,
            error: Infinity
        };

        if (points.length < 12) return fail;

        // 1. PCA ile torus eksenini bul
        const axis = this.findTorusAxis(points);
        if (!axis) return fail;

        // 2. Centroid hesapla
        const centroid = new THREE.Vector3();
        for (const p of points) centroid.add(p);
        centroid.divideScalar(points.length);

        // 3. Noktalari eksen diklemine projekte et -> radyal uzakliklar
        const radialDistances: number[] = [];
        const heights: number[] = [];

        for (const p of points) {
            const rel = p.clone().sub(centroid);
            const h = rel.dot(axis);
            const proj = rel.clone().sub(axis.clone().multiplyScalar(h));
            radialDistances.push(proj.length());
            heights.push(h);
        }

        // 4. Major radius = radyal uzakliklarin ortalamasi
        let sumR = 0;
        for (const r of radialDistances) sumR += r;
        const majorRadius = sumR / radialDistances.length;

        if (majorRadius < this.tolerance) return fail;

        // 5. Minor radius hesapla: her noktanin torus tube merkezinden uzakligi
        const tubeDistances: number[] = [];
        for (let i = 0; i < points.length; i++) {
            const dR = radialDistances[i] - majorRadius;
            const dH = heights[i];
            tubeDistances.push(Math.sqrt(dR * dR + dH * dH));
        }

        let sumMinor = 0;
        for (const d of tubeDistances) sumMinor += d;
        const minorRadius = sumMinor / tubeDistances.length;

        if (minorRadius < this.tolerance) return fail;
        if (majorRadius <= minorRadius * 1.1) return fail;

        // 6. Sapma hesaplama (noktalar torus yuzeyinde mi)
        let totalError = 0;
        for (const d of tubeDistances) {
            totalError += Math.pow(d - minorRadius, 2);
        }
        const error = Math.sqrt(totalError / points.length);

        return {
            center: centroid,
            axis,
            majorRadius,
            minorRadius,
            error
        };
    }

    /**
     * PCA ile torus simetri eksenini bul
     * Torus icin en kucuk varyans yonu = simetri ekseni
     */
    private findTorusAxis(points: THREE.Vector3[]): THREE.Vector3 | null {
        const centroid = new THREE.Vector3();
        for (const p of points) centroid.add(p);
        centroid.divideScalar(points.length);

        // Kovaryans matrisi
        let xx = 0, xy = 0, xz = 0;
        let yy = 0, yz = 0, zz = 0;
        for (const p of points) {
            const dx = p.x - centroid.x;
            const dy = p.y - centroid.y;
            const dz = p.z - centroid.z;
            xx += dx * dx; xy += dx * dy; xz += dx * dz;
            yy += dy * dy; yz += dy * dz;
            zz += dz * dz;
        }

        // En buyuk iki ozdegerin ozvektorlerini bul (power iteration + deflation)
        const cov = [
            [xx, xy, xz],
            [xy, yy, yz],
            [xz, yz, zz]
        ];

        // Ilk ozvektoru bul (en buyuk ozdeger)
        let v1 = new THREE.Vector3(1, 0.5, 0.3).normalize();
        for (let i = 0; i < 15; i++) {
            const nv = new THREE.Vector3(
                cov[0][0] * v1.x + cov[0][1] * v1.y + cov[0][2] * v1.z,
                cov[1][0] * v1.x + cov[1][1] * v1.y + cov[1][2] * v1.z,
                cov[2][0] * v1.x + cov[2][1] * v1.y + cov[2][2] * v1.z
            );
            if (nv.lengthSq() < 1e-20) return null;
            v1 = nv.normalize();
        }

        const lambda1 = v1.x * (cov[0][0] * v1.x + cov[0][1] * v1.y + cov[0][2] * v1.z) +
                         v1.y * (cov[1][0] * v1.x + cov[1][1] * v1.y + cov[1][2] * v1.z) +
                         v1.z * (cov[2][0] * v1.x + cov[2][1] * v1.y + cov[2][2] * v1.z);

        // Deflasyon: C' = C - lambda1 * v1 * v1^T
        const cov2 = [
            [cov[0][0] - lambda1 * v1.x * v1.x, cov[0][1] - lambda1 * v1.x * v1.y, cov[0][2] - lambda1 * v1.x * v1.z],
            [cov[1][0] - lambda1 * v1.y * v1.x, cov[1][1] - lambda1 * v1.y * v1.y, cov[1][2] - lambda1 * v1.y * v1.z],
            [cov[2][0] - lambda1 * v1.z * v1.x, cov[2][1] - lambda1 * v1.z * v1.y, cov[2][2] - lambda1 * v1.z * v1.z]
        ];

        // Ikinci ozvektoru bul
        let v2 = new THREE.Vector3(0.3, 1, 0.5).normalize();
        for (let i = 0; i < 15; i++) {
            const nv = new THREE.Vector3(
                cov2[0][0] * v2.x + cov2[0][1] * v2.y + cov2[0][2] * v2.z,
                cov2[1][0] * v2.x + cov2[1][1] * v2.y + cov2[1][2] * v2.z,
                cov2[2][0] * v2.x + cov2[2][1] * v2.y + cov2[2][2] * v2.z
            );
            if (nv.lengthSq() < 1e-20) return null;
            v2 = nv.normalize();
        }

        // Torus ekseni = v1 x v2 (en kucuk varyans yonu)
        const torusAxis = v1.clone().cross(v2).normalize();
        if (torusAxis.lengthSq() < 1e-10) return null;

        return torusAxis;
    }

    // ========== YARDIMCI METODLAR ==========

    private computeUAxis(normal: THREE.Vector3): THREE.Vector3 {
        // Normal'e dik bir U ekseni oluştur
        const arbitrary = Math.abs(normal.x) < 0.9
            ? new THREE.Vector3(1, 0, 0)
            : new THREE.Vector3(0, 1, 0);

        const u = new THREE.Vector3().crossVectors(normal, arbitrary).normalize();
        return u;
    }

    private computeVAxis(normal: THREE.Vector3): THREE.Vector3 {
        const u = this.computeUAxis(normal);
        const v = new THREE.Vector3().crossVectors(normal, u).normalize();
        return v;
    }
}
